import * as React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { useTranslation } from "react-i18next";
import countryList from "react-select-country-list";
import Select from "react-select";
import { getConfig, postDocumentCountry } from "../../api";
import { useNavigate } from "react-router-dom";
import { DOCUMENT_TYPES, US_STATES } from "../../constants";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

const DocumentCountryPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const documentCountry = undefined;

    const options = React.useMemo(() => countryList().getData(), []);
    const [showDocumentBox, setShowDocumentBox] = React.useState(false);
    const [showStateSelection, setShowStateSelection] = React.useState(false);
    const [showDisclaimerPopup, setShowDisclaimerPopup] = React.useState(false);
    const [config, setConfig] = React.useState({});
    const [country, setCountry] = React.useState(null);
    const [document, setDocument] = React.useState(null);
    const [documentState, setDocumentState] = React.useState(null);

    React.useEffect(() => {
        const getConfigRequest = async () => {
            let _config = await getConfig();
            setConfig(_config.data);
        };
        getConfigRequest();
    }, []);

    React.useEffect(() => {
        if (country && document) {
            let chosenDocument = config[country.value].find(
                (x) => x.document_type == document.value
            );

            if (!chosenDocument) {
                setShowDisclaimerPopup(true);
                return;
            }

            if (chosenDocument.supported_states) {
                setShowStateSelection(true);
            } else {
                postDocumentCountry(country.value, document.value).then(() => {
                    navigate("/validation");
                });
            }
        }
    }, [document]);

    React.useEffect(() => {
        if (country && document && documentState) {
            let chosenDocument = config[country.value].find(
                (x) => x.document_type == document.value
            );

            if (chosenDocument && chosenDocument.supported_states) {
                if (
                    chosenDocument.supported_states.includes(
                        documentState.value
                    )
                ) {
                    postDocumentCountry(
                        country.value,
                        document.value,
                        documentState.value
                    ).then(() => {
                        navigate("/validation");
                    });
                } else {
                    setShowDisclaimerPopup(true);
                }
            }
        }
    }, [documentState]);

    const onCountrySelect = async (country) => {
        setCountry(country);
        if (config[country.value]) {
            setShowDocumentBox(true);
        } else {
            await postDocumentCountry(country.value);
            navigate("/validation");
        }
    };

    const onDocumentSelect = async (document) => {
        setDocument(document);
    };

    const onDocumentStateSelect = async (state) => {
        setDocumentState(state);
    };

    const onModalCancelSelected = () => {
        setShowDisclaimerPopup(false);
        setDocument(null);
        setShowStateSelection(false);
        setDocumentState(null);
    };

    const onModalProceedSelected = () => {
        postDocumentCountry(
            country.value,
            document.value,
            documentState ? documentState.value : null
        ).then(() => {
            navigate("/validation");
        });
    };

    return (
        <Container component="main" maxWidth="sm">
            <CssBaseline />
            <Dialog
                open={showDisclaimerPopup}
                onClose={() => setShowDisclaimerPopup(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Disclaimer")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t(
                            "This document is not fully supported yet by our system. You may encounter delays in the validation process."
                        )}
                        <br />
                        {t(
                            "In order to avoid any delays, we strongly recommend you to provide a different document."
                        )}
                        <br />
                        <br />
                        {t(
                            "Here is the list of supported documents for your country:"
                        )}
                        <ul>
                            {country &&
                                config[country.value] &&
                                config[country.value].map((doc) => (
                                    <li key={doc.document_type}>
                                        {
                                            DOCUMENT_TYPES.filter(
                                                (d) =>
                                                    d.value ===
                                                    doc.document_type
                                            )[0].label
                                        }
                                        {config[country.value].find(
                                            (x) =>
                                                x.document_type ==
                                                doc.document_type
                                        ).supported_states && (
                                            <ul>
                                                {config[country.value]
                                                    .find(
                                                        (x) =>
                                                            x.document_type ==
                                                            doc.document_type
                                                    )
                                                    .supported_states.map(
                                                        (state) => (
                                                            <li key={state}>
                                                                {
                                                                    US_STATES.filter(
                                                                        (s) =>
                                                                            s.value ===
                                                                            state
                                                                    )[0].label
                                                                }
                                                            </li>
                                                        )
                                                    )}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                        </ul>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={onModalProceedSelected}
                        style={{
                            color: "#616161",
                        }}
                        variant="text"
                    >
                        Proceed with my document
                    </Button>
                    <Button
                        onClick={onModalCancelSelected}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        Change document
                    </Button>
                </DialogActions>
            </Dialog>
            <Box
                sx={{
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    boxShadow: "1px 2px 9px rgb(0 0 0 / 0.2)",
                    padding: "50px",
                    borderRadius: "10px",
                }}
            >
                <img
                    src="/assets/images/logo.png"
                    alt="logo"
                    style={{ width: "120px", marginBottom: "45px" }}
                />
                <Typography component="h1" variant="subtitle2">
                    {t(
                        "What country issued the document you'll provide us today ?"
                    )}
                </Typography>
                <Box sx={{ mt: 3 }} className="form-input">
                    <Select
                        options={options}
                        value={documentCountry}
                        onChange={onCountrySelect}
                        styles={{
                            control: (baseStyles, state) => ({
                                ...baseStyles,
                                height: "50px",
                            }),
                            valueContainer: (baseStyles, state) => ({
                                ...baseStyles,
                                height: "50px",
                            }),
                            input: (baseStyles, state) => ({
                                ...baseStyles,
                                height: "50px",
                            }),
                            indicatorsContainer: (baseStyles, state) => ({
                                ...baseStyles,
                                height: "50px",
                            }),
                        }}
                    />
                    {showDocumentBox && (
                        <>
                            <hr />
                            <Typography component="h1" variant="subtitle2">
                                {t("What type of document will you provide ?")}
                            </Typography>
                            <Select
                                options={DOCUMENT_TYPES}
                                value={document}
                                onChange={onDocumentSelect}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        height: "50px",
                                    }),
                                    valueContainer: (baseStyles, state) => ({
                                        ...baseStyles,
                                        height: "50px",
                                    }),
                                    input: (baseStyles, state) => ({
                                        ...baseStyles,
                                        height: "50px",
                                    }),
                                    indicatorsContainer: (
                                        baseStyles,
                                        state
                                    ) => ({
                                        ...baseStyles,
                                        height: "50px",
                                    }),
                                }}
                            />
                        </>
                    )}
                    {showStateSelection && (
                        <>
                            <hr />
                            <Typography component="h1" variant="subtitle2">
                                {t("What state issued the document ?")}
                            </Typography>
                            <Select
                                options={US_STATES}
                                onChange={onDocumentStateSelect}
                                value={documentState}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        height: "50px",
                                    }),
                                    valueContainer: (baseStyles, state) => ({
                                        ...baseStyles,
                                        height: "50px",
                                    }),
                                    input: (baseStyles, state) => ({
                                        ...baseStyles,
                                        height: "50px",
                                    }),
                                    indicatorsContainer: (
                                        baseStyles,
                                        state
                                    ) => ({
                                        ...baseStyles,
                                        height: "50px",
                                    }),
                                }}
                            />
                        </>
                    )}
                </Box>
            </Box>
        </Container>
    );
};

export default DocumentCountryPage;
